import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { BUTTON_LABELS, PERMISSIONS } from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { AccountType } from "@miplatform/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { VerticalTabsLayout } from "@mibuildweb/components";
import {
  useAccounts,
  useCommunities,
  useInspectionAuthorities,
  usePermissions,
} from "@mibuildweb/hooks";
import { Typography } from "@mui/material";
import { ConfigPageLayout } from "../../../../Config/ConfigPageLayout";
import {
  IAddRequirementsData,
  addRequirementsSchema,
} from "../schema/addRequirements.schema";
import { RequirementAttachItemsTab } from "../tabs/RequirementAttachItemsTab/RequirementAttachItemsTab";
import { RequirementDetailsTab } from "../tabs/RequirementDetailsTab/RequirementDetailsTab";
import { RequirementItemsTab } from "../tabs/RequirementItemsTab/RequirementItemsTab";
import { RequirementStepsTab } from "../tabs/RequirementStepsTab/RequirementStepsTab";
import { useAddRequirementsViewModel } from "./useAddRequirementsViewModel";

const INITIAL_VALUES: IAddRequirementsData = {
  name: "",
  type: "Inspection",
  inspectionAuthorityId: null,
  communityId: null,
  accountId: null,
  materialIds: [],
  materialAreas: {},
  materialQuantities: {},
  steps: [],
};

export function AddRequirements() {
  const { params, goBack } = useRouter();
  const { id: editId } = params;

  const form = useForm<IAddRequirementsData>({
    mode: "onChange",
    defaultValues: INITIAL_VALUES,
    resolver: zodResolver(addRequirementsSchema),
  });

  const {
    formState: { isValid, isDirty, touchedFields },
    reset,
    getValues,
    watch,
    trigger,
  } = form;

  const {
    name,
    type,
    inspectionAuthorityId,
    communityId,
    accountId,
    materialIds = [],
    materialAreas = {},
  } = watch();

  const isDetailsComplete =
    !!name &&
    ((type === "Inspection" && !!inspectionAuthorityId) ||
      (type === "Community" && !!communityId) ||
      (type === "Builder" && !!accountId));

  const { hasPermission } = usePermissions();
  const isReadOnly = editId
    ? !hasPermission(PERMISSIONS.REQUIREMENT.UPDATE)
    : !hasPermission(PERMISSIONS.REQUIREMENT.CREATE);

  const {
    inspectionAuthorities,
    getInspectionAuthorities,
    isLoading: isInspectionLoading,
  } = useInspectionAuthorities();

  const {
    communities,
    getCommunities,
    isLoading: isCommunitiesLoading,
  } = useCommunities();

  const {
    accounts,
    getAccounts,
    isLoading: isAccountsLoading,
  } = useAccounts({
    accountTypes: [AccountType.Builder],
  });

  const {
    fetchRequirementDetails,
    handleCreate,
    handleUpdate,
    isLoading: isViewModelLoading,
  } = useAddRequirementsViewModel({ goBack, reset, trigger });

  useEffect(() => {
    if (editId) {
      fetchRequirementDetails(editId);
    }
    getInspectionAuthorities();
    getCommunities();
    getAccounts();
  }, []);

  const isLoading =
    isInspectionLoading ||
    isCommunitiesLoading ||
    isAccountsLoading ||
    isViewModelLoading;

  const tabs = useMemo(
    () => [
      {
        label: "Details",
        value: "details",
        controlledFormViewComponent: RequirementDetailsTab,
        extraTabProps: {
          inspectionAuthorities,
          communities,
          accounts,
          isReadOnly,
        },
        isComplete: isDetailsComplete,
        hasErrors:
          !isDetailsComplete &&
          isDirty &&
          Object.keys(touchedFields).length > 0,
      },
      {
        label: "Items",
        value: "items",
        controlledFormViewComponent: RequirementItemsTab,
        extraTabProps: { isReadOnly },
        isComplete: materialIds.length > 0,
      },
      {
        label: "Attach Items to Areas",
        value: "attach-items",
        controlledFormViewComponent: RequirementAttachItemsTab,
        extraTabProps: { isReadOnly },
        hidden: materialIds.length === 0,
        isComplete:
          materialIds.length > 0 &&
          materialIds.every((id) => (materialAreas[id] ?? []).length > 0),
        hasErrors:
          isDirty &&
          materialIds.some((id) => (materialAreas[id] ?? []).length === 0),
      },
      {
        label: "Steps",
        value: "steps",
        controlledFormViewComponent: RequirementStepsTab,
        extraTabProps: { isReadOnly },
      },
    ],
    [
      isDetailsComplete,
      isDirty,
      touchedFields,
      materialIds,
      materialAreas,
      inspectionAuthorities,
      communities,
      accounts,
      isReadOnly,
    ]
  );

  const handleSubmit = () => {
    const data = getValues();
    if (editId) {
      handleUpdate(data);
    } else {
      handleCreate(data);
    }
  };

  const renderTitle = () => {
    const action = editId ? "Edit" : "Add";
    return (
      <Typography variant="HD4">
        {action} Requirement:{" "}
        <Typography variant="HD4" color="primary" component="span">
          {name || "New Requirement"}
        </Typography>
      </Typography>
    );
  };

  const actions = isReadOnly
    ? null
    : {
        cancel: {
          label: BUTTON_LABELS.CANCEL,
          onClick: () => goBack(),
        },
        submit: {
          label: editId ? "Update Requirement" : "Save Requirement",
          onClick: handleSubmit,
          disabled: !isDirty || !isValid || isLoading,
        },
      };

  return (
    <ConfigPageLayout
      renderTitle={renderTitle}
      bottomActions={actions}
      loading={isLoading}
    >
      <VerticalTabsLayout tabs={tabs} form={form} transparentBackground />
    </ConfigPageLayout>
  );
}
